import { isValidPayoutSortBy, PayoutColumnToSort } from "@/modules/finances/utils/financeUtils"
import type { ColumnSortingOrder } from "@/modules/types/datatableTypes"
import { isValidSortOrder } from "@/modules/ui/components/dataTables/datatableUtils"
import { useSetMultiParams } from "@/modules/utils/navigation/useQueryParam"
import { useServicesContext } from "@modules/core/services/services.context"
import { PayoutDetailsEntity, PayoutItemEntity } from "@modules/finances/financesPayout/entities/payoutEntity"
import { PayoutEnumApi } from "@modules/finances/financesPayout/gateway/payoutApi"
import { usePersistedDatatable } from "@modules/utils/hooks/useDataTable"
import { useQuery, useSuspenseQuery } from "@tanstack/react-query"
import { useSearchParams } from "react-router-dom"

export const usePayouts = (payoutEnumApi: PayoutEnumApi) => {
  const { payoutApi } = useServicesContext()

  const [searchParams] = useSearchParams()
  const setListParams = useSetMultiParams()
  const sortBy = searchParams.get("sortBy") ?? undefined
  const sortOrder = searchParams.get("order") ?? undefined
  const { page, setPage, size: pageSize, setSize: setPageSize } = usePersistedDatatable(20, true)

  const key = payoutEnumApi === PayoutEnumApi.OWNER ? "boxPayouts" : "userPayouts"
  const { data, isLoading } = useSuspenseQuery({
    queryKey: [key, { payoutEnumApi, page, pageSize, sortBy, sortOrder }],

    queryFn: async () => {
      if (sortBy && isValidPayoutSortBy(sortBy) && sortOrder && isValidSortOrder(sortOrder)) {
        return await payoutApi.getPayouts(payoutEnumApi, page, pageSize, sortBy, sortOrder)
      }
      return await payoutApi.getPayouts(payoutEnumApi, page, pageSize)
    },
  })

  const handlePreviousPage = () => {
    if (data.page > 0) {
      setPage((prev) => prev - 1)
    }
  }

  const handleNextPage = () => {
    if (data.page < data.totalPages - 1) {
      setPage((prev) => prev + 1)
    }
  }

  const setSorting = (field: PayoutColumnToSort, order: ColumnSortingOrder) => {
    if (field === sortBy && order === sortOrder) {
      setListParams([
        { key: "sortBy", value: undefined },
        { key: "order", value: undefined },
      ])
    } else {
      setListParams([
        { key: "sortBy", value: field },
        { key: "order", value: order },
      ])
    }
  }

  return {
    data: data.content.map((payout) => PayoutItemEntity.fromItemDto(payout)),
    isLoading,
    pageSize,
    setPageSize,
    handlePreviousPage,
    handleNextPage,
    hasPreviousPage: data.page > 0,
    hasNextPage: data.page < data.totalPages - 1,
    totalElements: data.totalSize,
    setSorting,
    sortBy,
    sortOrder,
  }
}

export const usePayout = (payoutEnumApi: PayoutEnumApi, payoutId: string | null) => {
  const { payoutApi } = useServicesContext()

  const key = payoutEnumApi === PayoutEnumApi.OWNER ? "boxPayout" : "userPayout"
  const { data, isLoading } = useQuery({
    queryKey: [key, payoutId, payoutEnumApi],

    queryFn: async () => {
      if (!payoutId) return null
      return await payoutApi.getPayout(payoutEnumApi, payoutId)
    },
  })

  return {
    data: data ? PayoutDetailsEntity.fromDto(data) : null,
    isLoading,
  }
}
